import React, { useState, useEffect, useCallback } from 'react';
import { UserPlus, Shield, Pencil, Trash2, X, Check, Ban, Wand2, Send } from 'lucide-react';
import { usersApi, employesApi, structuresApi } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { ROLE_LABELS } from '../../utils/permissions';
import { mdpConforme, genererMotDePasse } from '../../utils/motDePasse';
import ReglesMotDePasse from '../../components/ReglesMotDePasse';

// Compte rendu de l'envoi des 2 e-mails (invitation + mot de passe provisoire)
function messageEnvoi(envoi, email) {
  if (!envoi) return null;
  if (envoi.invitation && envoi.mot_de_passe) return `Invitation et mot de passe provisoire envoyés à ${email} (2 e-mails séparés).`;
  return `Compte enregistré, mais ${envoi.erreur || "l'e-mail n'a pas pu être envoyé."} Communiquez l'identifiant et le mot de passe autrement, ou utilisez « Renvoyer l'invitation » plus tard.`;
}

const ROLE_PILL = {
  admin: 'bg-navy-100 text-navy-700',
  direction: 'bg-gold-100 text-gold-700',
  equipe: 'bg-cream-300 text-navy-600',
  comptable: 'bg-cream-300 text-navy-600',
  salarie: 'bg-emerald-100 text-emerald-700',
  structure: 'bg-sky-100 text-sky-700',
};
const ROLE_DESC = {
  admin: 'Accès total',
  direction: 'Tout sauf le financier',
  equipe: 'Enfants, Transmissions, Planning',
  comptable: 'Facturation en lecture seule',
  salarie: 'Son espace RH et le planning équipe de sa crèche',
  structure: 'Quotidien des enfants de sa crèche (tablette)',
};
// Rôles proposés à la création d'un compte.
const ROLES_CHOISISSABLES = ['admin', 'direction', 'equipe', 'salarie', 'structure'];

function UserModal({ user, onClose, onSaved }) {
  const isNew = !user;
  const [form, setForm] = useState({
    nom: user?.nom || '', identifiant: user?.identifiant || '', email: user?.email || '',
    role: user?.role || 'equipe', password: '',
    employe_id: user?.employe_id || '', structure_id: user?.structure_id || '',
    acces_creche: !!user?.acces_creche,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [employes, setEmployes] = useState([]);
  const [structures, setStructures] = useState([]);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Listes de rattachement (salarié ↔ fiche RH, compte de crèche ↔ structure)
  useEffect(() => {
    employesApi.getAll().then(r => setEmployes(r.data || [])).catch(() => {});
    structuresApi.getAll().then(r => setStructures(r.data || [])).catch(() => {});
  }, []);

  const emailValide = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const mdpOk = form.password ? mdpConforme(form.password) : !isNew;

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      const rattachements = {
        employe_id: form.role === 'salarie' ? Number(form.employe_id) || null : null,
        structure_id: form.role === 'structure' ? Number(form.structure_id) || null : null,
        acces_creche: form.role === 'salarie' && form.acces_creche ? 1 : 0,
      };
      if (isNew) {
        const { data } = await usersApi.create({ ...form, ...rattachements });
        window.alert(messageEnvoi(data.envoi, form.email.trim()));
      }
      else await usersApi.update(user.id, { nom: form.nom, identifiant: form.identifiant, email: form.email, role: form.role, ...rattachements, ...(form.password ? { password: form.password } : {}) });
      onSaved(); onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur'); setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-backdrop" onClick={onClose}>
      <div className="bg-white rounded-[22px] shadow-2xl w-full max-w-md animate-sheet" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <h3 className="font-serif text-[20px] text-navy-900">{isNew ? 'Nouveau compte' : 'Modifier le compte'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-navy-50 hover:bg-navy-100 flex items-center justify-center text-navy-400"><X size={17} /></button>
        </div>
        <div className="px-6 pb-6 space-y-3">
          <div>
            <label className="block text-[13px] font-medium text-navy-600 mb-1">Nom complet</label>
            <input className="form-input rounded-xl" value={form.nom} onChange={e => upd('nom', e.target.value)} placeholder="Ex : Sophie Martin" />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-navy-600 mb-1">Identifiant</label>
            <input className="form-input rounded-xl" value={form.identifiant} onChange={e => upd('identifiant', e.target.value)} placeholder="prénom (minuscules, sans espace)" />
          </div>
          <div>
            <label className="block text-[13px] font-medium text-navy-600 mb-1">Adresse email</label>
            <input type="email" className="form-input rounded-xl" value={form.email} onChange={e => upd('email', e.target.value)} placeholder="prenom.nom@exemple.fr" />
            <p className="text-[12px] text-navy-400 mt-1">{isNew ? "Obligatoire : l'invitation à se connecter puis le mot de passe provisoire y sont envoyés dans 2 e-mails séparés." : 'Nécessaire pour que « Mot de passe oublié ? » fonctionne pour cet utilisateur.'}</p>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-navy-600 mb-1">Profil d'accès</label>
            <div className="grid grid-cols-1 gap-2">
              {ROLES_CHOISISSABLES.map(r => (
                <button key={r} type="button" onClick={() => upd('role', r)}
                  className={`text-left px-3 py-2 rounded-xl border transition-colors ${form.role === r ? 'border-navy-700 bg-navy-50' : 'border-cream-300 hover:bg-cream-100'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-navy-800 text-sm">{ROLE_LABELS[r]}</span>
                    {form.role === r && <Check size={15} className="text-navy-700" />}
                  </div>
                  <div className="text-[12px] text-navy-400">{ROLE_DESC[r]}</div>
                </button>
              ))}
            </div>
          </div>
          {form.role === 'salarie' && (
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1">Fiche salarié rattachée</label>
              <select className="form-input rounded-xl" value={form.employe_id} onChange={e => upd('employe_id', e.target.value)}>
                <option value="">— Choisir un salarié —</option>
                {employes.map(e => <option key={e.id} value={e.id}>{e.prenom} {e.nom} — {e.poste || 'poste non précisé'}</option>)}
              </select>
              <p className="text-[12px] text-navy-400 mt-1">« Mon espace » (congés, heures, planning personnel) et le planning de l'équipe de sa crèche, en consultation.</p>
              <label className="mt-3 flex items-start gap-2.5 px-3 py-2.5 rounded-xl border border-cream-300 hover:bg-cream-100 cursor-pointer">
                <input type="checkbox" className="mt-0.5" checked={form.acces_creche} onChange={e => upd('acces_creche', e.target.checked)} />
                <span>
                  <span className="block text-sm font-medium text-navy-800">Accès au compte crèche</span>
                  <span className="block text-[12px] text-navy-400">Ajoute le quotidien des enfants de SA crèche : transmissions, suivi du développement, suivi médical, parents, planning enfants, documents de santé.</span>
                </span>
              </label>
            </div>
          )}
          {form.role === 'structure' && (
            <div>
              <label className="block text-[13px] font-medium text-navy-600 mb-1">Crèche rattachée</label>
              <select className="form-input rounded-xl" value={form.structure_id} onChange={e => upd('structure_id', e.target.value)}>
                <option value="">— Choisir une structure —</option>
                {structures.map(st => <option key={st.id} value={st.id}>{st.nom}</option>)}
              </select>
              <p className="text-[12px] text-navy-400 mt-1">Compte partagé de la tablette : enfants, transmissions et protocoles de cette crèche uniquement.</p>
            </div>
          )}
          <div>
            <label className="block text-[13px] font-medium text-navy-600 mb-1">{isNew ? 'Mot de passe provisoire' : 'Nouveau mot de passe provisoire (optionnel)'}</label>
            <div className="flex gap-2">
              <input type="text" autoComplete="off" spellCheck={false} className="form-input rounded-xl font-mono flex-1" value={form.password} onChange={e => upd('password', e.target.value)} placeholder={isNew ? '12 caractères minimum' : 'laisser vide pour ne pas changer'} />
              <button type="button" onClick={() => upd('password', genererMotDePasse())} className="apple-btn-secondary whitespace-nowrap" title="Générer un mot de passe robuste">
                <Wand2 size={15} /> Générer
              </button>
            </div>
            {(isNew || form.password) && <ReglesMotDePasse password={form.password} />}
            <p className="text-[12px] text-navy-400 mt-1">L'utilisateur devra le remplacer par son propre mot de passe à sa première connexion.</p>
          </div>
          {error && <div className="text-[13px] text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="apple-btn-secondary">Annuler</button>
            <button onClick={submit} disabled={saving || !form.identifiant || !mdpOk || (isNew && !emailValide)} className="apple-btn-primary">
              {saving ? 'Enregistrement…' : isNew ? 'Créer et envoyer l\'invitation' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function UtilisateursPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { user } | { user: null } | null

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await usersApi.getAll(); setUsers(data); } catch { /* noop */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Ouvre / ferme le compte de sa crèche à un compte salarié, directement depuis la liste.
  const toggleCreche = async (u) => {
    try { await usersApi.update(u.id, { acces_creche: u.acces_creche ? 0 : 1 }); load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };
  const toggleActif = async (u) => {
    try { await usersApi.update(u.id, { actif: u.actif ? 0 : 1 }); load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };
  // Nouveau mot de passe provisoire + renvoi des 2 e-mails (compte jamais activé, mot de passe perdu…)
  const renvoyer = async (u) => {
    if (!window.confirm(`Renvoyer l'invitation à ${u.email} ? Un nouveau mot de passe provisoire sera généré et l'ancien ne fonctionnera plus.`)) return;
    try {
      const { data } = await usersApi.renvoyerInvitation(u.id, genererMotDePasse());
      alert(messageEnvoi(data.envoi, u.email));
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Supprimer le compte de ${u.nom || u.identifiant} ?`)) return;
    try { await usersApi.delete(u.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  return (
    <div className="-m-6 p-6 min-h-full bg-cream-200">
      <div className="max-w-3xl mx-auto pb-16">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="font-serif text-[32px] leading-tight text-navy-900">Comptes &amp; accès</h1>
            <div className="w-12 h-0.5 bg-gold-400 rounded-full mt-2 mb-2.5" />
            <p className="text-navy-400 text-[14px]">Gérez les utilisateurs et leur profil d'accès.</p>
          </div>
          <button onClick={() => setModal({ user: null })} className="apple-btn-gold">
            <UserPlus size={16} /> Nouveau compte
          </button>
        </div>

        {loading ? (
          <div className="text-center text-navy-300 py-16">Chargement…</div>
        ) : (
          <div className="apple-card divide-y divide-cream-300/50 overflow-hidden">
            {users.map(u => (
              <div key={u.id} className="px-5 py-3.5 flex items-center gap-3.5">
                <div className="w-10 h-10 rounded-full bg-navy-100 text-navy-700 flex items-center justify-center font-semibold text-[13px] flex-shrink-0">
                  {(u.nom || u.identifiant || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-navy-900">{u.nom || u.identifiant}</span>
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${ROLE_PILL[u.role]}`}>{ROLE_LABELS[u.role] || u.role}</span>
                    {!u.actif && <span className="text-[11px] px-2 py-0.5 rounded-full bg-navy-50 text-navy-300">désactivé</span>}
                    {u.role === 'salarie' && (
                      <button type="button" onClick={() => toggleCreche(u)}
                        title={u.acces_creche ? 'Retirer l\'accès au compte crèche' : 'Donner l\'accès au compte crèche'}
                        className={`text-[11px] px-2 py-0.5 rounded-full font-medium border transition-colors ${u.acces_creche ? 'bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-200' : 'bg-white text-navy-300 border-cream-300 hover:bg-cream-100'}`}>
                        {u.acces_creche ? '✓ Compte crèche' : '+ Compte crèche'}
                      </button>
                    )}
                    {u.id === me?.id && <span className="text-[11px] text-gold-600">vous</span>}
                  </div>
                  <div className="text-[12.5px] text-navy-300 mt-0.5">
                    @{u.identifiant}{u.email ? ` · ${u.email}` : ''} · {ROLE_DESC[u.role] || u.role}
                    {u.employe_nom ? ` · fiche RH : ${u.employe_nom}` : ''}
                    {u.structure_nom ? ` · ${u.structure_nom}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => toggleActif(u)} title={u.actif ? 'Désactiver' : 'Réactiver'}
                    className="p-2 rounded-lg text-navy-300 hover:bg-navy-50 hover:text-navy-700">
                    {u.actif ? <Ban size={16} /> : <Check size={16} />}
                  </button>
                  {u.email && u.id !== me?.id && (
                    <button onClick={() => renvoyer(u)} title="Renvoyer l'invitation (nouveau mot de passe provisoire)"
                      className="p-2 rounded-lg text-navy-300 hover:bg-navy-50 hover:text-navy-700"><Send size={16} /></button>
                  )}
                  <button onClick={() => setModal({ user: u })} className="p-2 rounded-lg text-navy-300 hover:bg-navy-50 hover:text-navy-700"><Pencil size={16} /></button>
                  <button onClick={() => remove(u)} className="p-2 rounded-lg text-navy-200 hover:bg-red-50 hover:text-red-500"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-start gap-2 text-[12.5px] text-navy-400 bg-cream-100 border border-cream-300/60 rounded-xl px-4 py-3">
          <Shield size={15} className="text-gold-500 flex-shrink-0 mt-0.5" />
          <span><b className="text-navy-600">Administrateur</b> : accès total · <b className="text-navy-600">Direction</b> : tout sauf le financier (Finance, Analyse, Tarifs, marges) · <b className="text-navy-600">Équipe</b> : Enfants, Transmissions, Planning · <b className="text-navy-600">Salarié</b> : son espace RH personnel (congés, heures, planning) et le planning équipe de sa crèche, + le compte crèche si vous le cochez · <b className="text-navy-600">Crèche</b> : compte partagé de la tablette, limité aux enfants de sa structure.</span>
        </div>

        {modal && <UserModal user={modal.user} onClose={() => setModal(null)} onSaved={load} />}
      </div>
    </div>
  );
}
